/**
 * Purchase Prices Tab Component
 *
 * Displays purchase prices from all suppliers for a raw product.
 * Highlights the best price per recipe unit.
 * @see US-8.11 - Prix d'Achat dans Drawer Produit
 */

import { useNavigate } from 'react-router-dom';
import { Star, Building2, AlertCircle, ExternalLink } from 'lucide-react';
import { Skeleton, Badge } from '@/components/ui';
import { usePurchasePrices } from '../../hooks/useRawProducts';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { useCurrency } from '@/hooks';
import type { RawProductPurchasePrice } from '@bstock/shared';

interface PurchasePricesTabProps {
  productId: string;
  recipeUnitSymbol: string;
}

/**
 * Group purchase prices by supplier
 */
interface SupplierGroup {
  supplierId: string;
  supplierName: string;
  supplierIsActive: boolean;
  items: RawProductPurchasePrice[];
}

function groupBySupplier(prices: RawProductPurchasePrice[]): SupplierGroup[] {
  const groups = new Map<string, SupplierGroup>();

  for (const price of prices) {
    if (!groups.has(price.supplierId)) {
      groups.set(price.supplierId, {
        supplierId: price.supplierId,
        supplierName: price.supplierName,
        supplierIsActive: price.supplierIsActive,
        items: [],
      });
    }
    groups.get(price.supplierId)!.items.push(price);
  }

  // Sort groups by supplier name
  return Array.from(groups.values()).sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName)
  );
}

export function PurchasePricesTab({ productId, recipeUnitSymbol }: PurchasePricesTabProps) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const { data: prices, isLoading, error } = usePurchasePrices(productId);

  const handleSupplierClick = (supplierId: string) => {
    navigate(`/parametres/suppliers/${supplierId}/catalog?tab=suppliers`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-10 h-10 text-danger-500 mb-3" />
        <p className="text-danger-500 font-medium">Erreur lors du chargement</p>
        <p className="text-sm text-neutral-500 mt-1">{error.message}</p>
      </div>
    );
  }

  if (!prices || prices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Building2 className="w-12 h-12 text-neutral-300 mb-4" />
        <p className="text-neutral-500 font-medium">Aucun fournisseur reference</p>
        <p className="text-sm text-neutral-400 mt-1 max-w-sm">
          Ce produit n'est pas encore reference dans le catalogue d'un fournisseur.
          Ajoutez-le depuis la page Fournisseurs.
        </p>
      </div>
    );
  }

  const supplierGroups = groupBySupplier(prices);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-primary-700 mb-1">
          <Star className="w-4 h-4 fill-primary-500" />
          <span className="font-medium">Meilleur prix</span>
        </div>
        <p className="text-sm text-primary-600">
          Les conditionnements avec le meilleur prix a l'unite de recette ({recipeUnitSymbol}) sont mis en evidence.
        </p>
      </div>

      {/* Supplier Groups */}
      {supplierGroups.map((group) => (
        <div
          key={group.supplierId}
          className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
        >
          {/* Supplier Header */}
          <button
            onClick={() => handleSupplierClick(group.supplierId)}
            className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200 hover:bg-neutral-100 transition-colors cursor-pointer text-left"
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-neutral-500" />
              <span className="font-medium text-neutral-900">{group.supplierName}</span>
              {!group.supplierIsActive && (
                <Badge variant="neutral">Inactif</Badge>
              )}
              <ExternalLink className="w-3.5 h-3.5 text-neutral-400" />
            </div>
            <span className="text-xs text-neutral-500">
              {group.items.length} conditionnement{group.items.length > 1 ? 's' : ''}
            </span>
          </button>

          {/* Packaging Items */}
          <div className="divide-y divide-neutral-100">
            {group.items.map((item) => (
              <div
                key={item.catalogItemId}
                className={`flex items-center justify-between px-4 py-3 ${
                  item.isBestPrice ? 'bg-success-50/50' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Packaging Info */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-neutral-900">
                        {item.packaging.name}
                      </span>
                      {item.isBestPrice && (
                        <Badge variant="success" className="gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          Meilleur prix
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {formatQuantity(item.packaging.quantity)} {item.packaging.unit.symbol} = {formatQuantity(item.conversionToRecipeUnit)} {recipeUnitSymbol}
                    </p>
                  </div>
                </div>

                {/* Prices */}
                <div className="flex items-center gap-6 text-right">
                  {/* Package Price */}
                  <div>
                    <p className="font-semibold text-neutral-900">
                      {formatCentimes(item.priceExclTax, currency)}
                    </p>
                    <p className="text-xs text-neutral-500">HT/{item.packaging.name}</p>
                  </div>

                  {/* Unit Price (display unit: kg, L, pce) */}
                  <div className="min-w-[80px]">
                    <p className={`text-sm ${item.isBestPrice ? 'text-success-700 font-medium' : 'text-neutral-600'}`}>
                      {formatCentimes(item.displayUnitPrice, currency)}
                    </p>
                    <p className="text-xs text-neutral-500">/{item.displayUnitSymbol}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Info */}
      <p className="text-xs text-neutral-400 text-center">
        Les prix affiches sont les prix actuels des catalogues fournisseurs.
      </p>
    </div>
  );
}
